import {ReactNode, useCallback} from 'react';

import {
  MessageLinkInterceptProvider,
  WidgetAPI,
} from '@stripe-internal/conversations-ui';

import {ConversationsUIProvidersBase} from '../../core/providers/ConversationsUIProviders';
import {ThreadsFileUploadProvider} from './ThreadsFileUploadProvider';
import {
  useThreadsAnalytics,
  useThreadsApp,
  useThreadsConfig,
  useThreadsController,
  useThreadsConversationsClient,
  useThreadsParentViewportSnapshot,
} from '../app/hooks';

// Assistant messages can embed a markdown link with this href to switch the
// user to another conversation thread (e.g. the blocking message shown when
// live support is already active in another thread).
const CONTINUE_CONVERSATION_HREF_PREFIX = '#continue-conversation=';

export function ThreadsConversationsUIProviders({
  children,
}: {
  children: ReactNode;
}) {
  const app = useThreadsApp();
  const controller = useThreadsController();
  const config = useThreadsConfig();
  const analytics = useThreadsAnalytics();
  const client = useThreadsConversationsClient();
  const parentViewportSnapshot = useThreadsParentViewportSnapshot();

  const actionHandler = useCallback<WidgetAPI['requestAction']>(
    (action, opts) => controller.ui.requestAction({action, ...opts}),
    [controller],
  );

  const handleMessageLinkClick = useCallback(
    (href: string) => {
      if (!href.startsWith(CONTINUE_CONVERSATION_HREF_PREFIX)) {
        return false;
      }

      const conversationId = href.slice(
        CONTINUE_CONVERSATION_HREF_PREFIX.length,
      );
      if (!conversationId) {
        return false;
      }

      app.selectConversation(conversationId);
      return true;
    },
    [app],
  );

  return (
    <ConversationsUIProvidersBase
      analytics={analytics}
      client={client}
      config={config}
      actionHandler={actionHandler}
      FileUploadProviderComponent={ThreadsFileUploadProvider}
      parentViewportSnapshot={parentViewportSnapshot}
    >
      <MessageLinkInterceptProvider onMessageLinkClick={handleMessageLinkClick}>
        {children}
      </MessageLinkInterceptProvider>
    </ConversationsUIProvidersBase>
  );
}
